// src/registered/common/company/CompanyContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { fetchCorporateHierarchy } from "../../corporate_details/corporateInteractionService";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";

const STORAGE_KEY = "SEC_ACTIVE_COMPANY";

const CompanyContext = createContext(null);

function readStoredCompany() {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function CompanyProvider({ children }) {
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const stored = readStoredCompany();

  const [activeCompanyId, setActiveCompanyId] = useState(stored?.id ?? null);
  const [activeCompanyName, setActiveCompanyName] = useState(stored?.name ?? null);
  const [scopedEntityIds, setScopedEntityIds] = useState(stored?.scopedEntityIds ?? []);

  const persist = (id, name, scoped) => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ id, name, scopedEntityIds: scoped })
    );
  };

  /**
   * 🎯 Sets the active company and resolves its full connected hierarchy
   * (parents, children, co-owned entities) so downstream screens can scope
   * actions to "this company or its siblings/peers" without a second lookup.
   */
  const setActiveCompany = useCallback(async (id, name) => {
    setActiveCompanyId(id);
    setActiveCompanyName(name);
    persist(id, name, []);

    const tree = await fetchCorporateHierarchy(id, { siteKey });
    const ids = [
      String(id),
      ...tree
        .map((node) => GoldenSourceHelper.get(node, "CorporateEntityId"))
        .filter((v) => v !== null && v !== undefined)
        .map(String),
    ];
    const scoped = [...new Set(ids)];
    setScopedEntityIds(scoped);
    persist(id, name, scoped);
  }, [siteKey]);

  const clearActiveCompany = useCallback(() => {
    setActiveCompanyId(null);
    setActiveCompanyName(null);
    setScopedEntityIds([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = {
    activeCompanyId,
    activeCompanyName,
    scopedEntityIds,
    setActiveCompany,
    clearActiveCompany,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanyContext() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error("useCompanyContext must be used within a CompanyProvider");
  }
  return context;
}
